/**
 * Recovering the printed object's shape from the path that printed it.
 *
 * A slicer produces a toolpath by intersecting a solid with a horizontal plane at each
 * layer height and walking the outline that falls out. So the outline is still in there:
 * each layer's outer perimeter *is* a cross-section of the original object. Everything
 * else the head does — infill, the second and third walls, the skirt — is a consequence of
 * how the thing gets built rather than of what it is.
 *
 * This undoes that. It reads a bag of extruded line segments and returns, per layer, the
 * outline of the object and of any holes through it — which is what the original solid
 * would have been sliced from.
 *
 * Pure, and free of any rendering, so the fiddly parts can be tested at a bench rather
 * than by looking at a picture and forming an opinion.
 */

/** A point on the bed, in scene axes: across, and away from the viewer. */
export type Point = readonly [x: number, z: number];

export interface Contour {
  /** Height above the bed, in millimetres. */
  readonly height: number;
  /**
   * The outline, as a closed ring. The last point is not a repeat of the first: closing
   * it is the reader's job, and leaving it open means every point is a real one.
   */
  readonly points: readonly Point[];
  /** Enclosed area, always positive. What tells an outline from a hole through it. */
  readonly area: number;
  /** Middle of the outline, for recognising it again on the layer above. */
  readonly centre: Point;
  /** Whether this is the edge of the object, or the edge of a hole through it. */
  readonly kind: 'outline' | 'hole';
}

/**
 * How far apart two points can be and still count as the same place, in millimetres.
 *
 * A bead of plastic is about 0.4 mm wide, so a path that comes back within that has
 * closed on itself as far as the printed object is concerned.
 */
const SAME_PLACE_MM = 0.45;

/**
 * How near its start a run must return to count as closed, in millimetres.
 *
 * Far tighter than joining, and the difference matters. A perimeter closes exactly — the
 * last move ends where the first began — so nothing is lost by insisting on it. But infill
 * is a zigzag whose passes lie a bead apart, so each return pass comes back *near* where
 * the previous one started. Judged by the looser figure, every pair of passes counts as a
 * closed loop: tens of thousands of slivers per print, each then measured, grouped and
 * stacked. It is the difference between rebuilding a print in a moment and not finishing.
 */
const CLOSES_MM = 0.15;

/**
 * Smallest outline worth keeping, in square millimetres.
 *
 * A guard against slivers: a loop enclosing almost nothing is a scrap of path that
 * happened to come back on itself, not a cross-section of anything. Small enough to keep a
 * hole a millimetre across.
 */
const LEAST_AREA_MM2 = 0.5;

/** Heights within this of each other are one layer. Finer than any layer height. */
const SAME_HEIGHT_MM = 0.01;

/**
 * How far a run may climb between one move and the next and still be one run.
 *
 * Not zero, because of vase mode: printed as a single unbroken spiral, the head rises
 * continuously and never returns to a height it has been at. Broken at every change it
 * would come out as no runs at all and the file could not be rebuilt — yet in plan view
 * each turn of the spiral closes on itself perfectly well, which is all that is needed.
 * Bigger than a layer, smaller than a Z-hop, which really does end a run.
 */
const CLIMB_MM = 1;

/**
 * Outlines whose areas are within this of each other are the same feature.
 *
 * A slicer prints two or three walls one beside the next, each a closed loop a bead
 * further in than the last. They are all the same edge of the same object and only the
 * outermost is on its surface, so the rest have to go — drawn, they are a shell inside a
 * shell, and every one of them costs as much as the one that can be seen.
 */
const SAME_FEATURE_RATIO = 0.82;

/** How far a feature may drift between layers and still be the same feature, in mm. */
const DRIFT_MM = 4;

/**
 * Fewest layers a stack of outlines must span to be part of the object.
 *
 * A skirt is one loop on one layer, printed round the object to prime the nozzle, and it
 * is larger than anything on the object itself — so left in, it would be taken for the
 * outline and the model would come out as a wall around a hollow.
 *
 * Applied against the tallest stack rather than on its own, because "too short to be real"
 * only means anything next to something taller. A print that is genuinely two layers tall
 * is all short stacks, and refusing to show it would be worse than showing its skirt.
 */
const FEWEST_LAYERS = 3;

export interface ContourOptions {
  /**
   * How far an outline may be moved to simplify it, in millimetres.
   *
   * A curve arrives as a great many short moves. Below the width of a bead, the difference
   * cannot be seen on the printed object either.
   */
  readonly tolerance?: number;
}

/**
 * A stack of the same outline through successive layers.
 *
 * The unit the surface is built from: consecutive members are joined into a band, so a
 * chain is one continuous piece of the object's skin.
 */
export interface Chain {
  readonly kind: 'outline' | 'hole';
  readonly contours: readonly Contour[];
}

/**
 * Find the object's outlines, layer by layer, and stack them into chains.
 *
 * @param segments Extruded moves as pairs of points: six numbers each, in scene axes.
 */
export function extractChains(
  segments: Float32Array,
  options: ContourOptions = {},
): Chain[] {
  const tolerance = options.tolerance ?? 0.05;

  const byHeight = new Map<number, Point[][]>();
  for (const run of toRuns(segments)) {
    if (!isClosed(run.points)) continue;

    const key = Math.round(run.height / SAME_HEIGHT_MM);
    const loops = byHeight.get(key);
    if (loops) loops.push(run.points);
    else byHeight.set(key, [run.points]);
  }

  const layers = [...byHeight.entries()]
    .sort(([a], [b]) => a - b)
    .map(([key, loops]) => chooseContours(loops, key * SAME_HEIGHT_MM, tolerance));

  return intoChains(layers);
}

/** A run of moves that join end to end, all at one height. */
interface Run {
  readonly height: number;
  readonly points: Point[];
}

/**
 * Split the segments into runs that join end to end.
 *
 * A travel breaks the chain, which is what separates one printed feature from the next:
 * the head lifts, goes somewhere else, and starts again. A change of height breaks it too,
 * so that a spiral — where the head rises as it goes — does not come out as one enormous
 * run belonging to no layer.
 */
export function toRuns(segments: Float32Array): Run[] {
  const runs: Run[] = [];
  let points: Point[] = [];
  let height = 0;
  /** Where the head got to, which is what the next move has to follow on from. */
  let reached = 0;

  const flush = (): void => {
    if (points.length > 2) runs.push({ height, points });
    points = [];
  };

  for (let at = 0; at + 5 < segments.length; at += 6) {
    const ax = segments[at] ?? 0;
    const ay = segments[at + 1] ?? 0;
    const az = segments[at + 2] ?? 0;
    const bx = segments[at + 3] ?? 0;
    const by = segments[at + 4] ?? 0;
    const bz = segments[at + 5] ?? 0;

    // A hop, or a move up to the next layer: not part of any one outline.
    if (Math.abs(ay - by) > CLIMB_MM) {
      flush();
      continue;
    }

    const tail = points[points.length - 1];
    const joins =
      tail !== undefined &&
      Math.abs(reached - ay) <= CLIMB_MM &&
      distance(tail, [ax, az]) < SAME_PLACE_MM;

    if (!joins) {
      flush();
      height = ay;
      points = [[ax, az]];
    }

    reached = by;

    points.push([bx, bz]);

    // A run ends the moment it arrives back where it began. Slicers often go straight
    // from one wall into the next without lifting the head, and without this the two come
    // out as a single run going round twice — which has the area of both put together, so
    // it is neither wall and matches nothing on the layer above.
    const start = points[0];
    if (start && points.length > 3 && distance(start, [bx, bz]) < CLOSES_MM) {
      // Ended outright, rather than carried on from where it closed. Whatever comes next
      // begins a run of its own at its own height — otherwise the first move of the layer
      // above joins on here and is recorded at this layer's height, and every outline
      // ends up attributed one layer too low.
      flush();
      points = [];
    }
  }

  flush();
  return runs;
}

/** Whether a run comes back to where it started. */
function isClosed(points: readonly Point[]): boolean {
  const first = points[0];
  const last = points[points.length - 1];
  return first !== undefined && last !== undefined && distance(first, last) < CLOSES_MM;
}

/**
 * Pick out the object's edge, and the edges of holes through it, from one layer's loops.
 *
 * The largest loop is the outside of the object. Anything inside it is a hole — but a hole
 * is printed with walls too, and those walls run *outwards* into the material, so of a
 * hole's several loops the smallest is the one on the surface. That asymmetry is the whole
 * of the rule: outermost wins on the outside, innermost wins inside.
 */
function chooseContours(
  loops: readonly Point[][],
  height: number,
  tolerance: number,
): Contour[] {
  const measured = loops
    .map((points) => simplifyRing(points, tolerance))
    .filter((points) => points.length >= 3)
    .map((points) => ({ points, area: areaOf(points), centre: centreOf(points) }))
    .filter((loop) => loop.area >= LEAST_AREA_MM2)
    .sort((a, b) => b.area - a.area);

  const outer = measured[0];
  if (!outer) return [];

  const contours: Contour[] = [
    {
      height,
      points: outer.points,
      area: outer.area,
      centre: outer.centre,
      kind: 'outline',
    },
  ];

  // Everything else is either another wall of the outside, or part of a hole. Group what
  // is left by size and position; each group is one feature printed several times over.
  const holes: { points: Point[]; area: number; centre: Point }[] = [];

  for (const loop of measured.slice(1)) {
    if (loop.area > outer.area * SAME_FEATURE_RATIO) continue; // another wall of the outside

    const existing = holes.findIndex(
      (hole) =>
        distance(hole.centre, loop.centre) < DRIFT_MM &&
        Math.min(hole.area, loop.area) / Math.max(hole.area, loop.area) >
          SAME_FEATURE_RATIO,
    );

    if (existing === -1) {
      holes.push(loop);
    } else if (loop.area < (holes[existing]?.area ?? Infinity)) {
      // Smaller: nearer the surface of the hole than the one already held.
      holes[existing] = loop;
    }
  }

  for (const hole of holes) {
    contours.push({
      height,
      points: hole.points,
      area: hole.area,
      centre: hole.centre,
      kind: 'hole',
    });
  }

  return contours;
}

/**
 * Follow each outline up through the layers.
 *
 * Matched by where it is and how big it is, which is enough because a layer is a fifth of
 * a millimetre and nothing moves far in that. What cannot be matched starts a new chain,
 * so a shape that splits into two partway up comes out as two.
 */
function intoChains(layers: readonly Contour[][]): Chain[] {
  const finished: Chain[] = [];
  let growing: { kind: 'outline' | 'hole'; contours: Contour[] }[] = [];

  for (const layer of layers) {
    const carried: typeof growing = [];
    const spare = [...layer];

    for (const chain of growing) {
      const last = chain.contours[chain.contours.length - 1];
      const at = last === undefined ? -1 : spare.findIndex((c) => continues(last, c));

      if (at === -1) {
        finished.push(chain);
        continue;
      }

      const [next] = spare.splice(at, 1);
      if (next) chain.contours.push(next);
      carried.push(chain);
    }

    for (const orphan of spare) {
      carried.push({ kind: orphan.kind, contours: [orphan] });
    }

    growing = carried;
  }

  finished.push(...growing);

  const tallest = finished.reduce(
    (most, chain) => Math.max(most, chain.contours.length),
    0,
  );

  return finished
    .filter((chain) => chain.contours.length >= Math.min(FEWEST_LAYERS, tallest))
    .map((chain) => ({ kind: chain.kind, contours: chain.contours }));
}

/**
 * Whether one layer's outline carries on from the layer below.
 *
 * The area test is tight because layers are a fifth of a millimetre apart, and nothing
 * real changes much in that: even a steeply tapered cone loses under two per cent of its
 * area from one layer to the next. Loose, it lets a skirt — a lone loop printed round the
 * object on the first layer, and larger than the object — be taken for the start of the
 * object's own stack, and the model comes out the size of the skirt.
 *
 * A genuine step in the model does break the chain here, which is the right answer: the
 * two stacks are then capped where they meet, and those caps are the ledge.
 */
function continues(last: Contour, next: Contour): boolean {
  if (last.kind !== next.kind) return false;
  if (distance(last.centre, next.centre) > DRIFT_MM) return false;

  const ratio = Math.min(last.area, next.area) / Math.max(last.area, next.area);
  return ratio > 0.7;
}

/**
 * Thin out an outline without changing its shape, by Ramer–Douglas–Peucker.
 *
 * Cut in two before simplifying, because the ends of a closed ring are in the same place:
 * the line between them has no length, every point measures as lying on it, and the whole
 * ring collapses to a couple of points. Splitting at the far side gives two arcs, each
 * with ends that are properly apart.
 */
export function simplifyRing(points: readonly Point[], tolerance: number): Point[] {
  const open = isClosed(points) ? points.slice(0, -1) : [...points];
  if (open.length < 4 || tolerance <= 0) return [...open];

  const half = Math.floor(open.length / 2);
  const front = simplifyLine(open.slice(0, half + 1), tolerance);
  const back = simplifyLine(open.slice(half), tolerance);

  // The two arcs share the point they were cut at, so one copy of it goes; the ring
  // closes from the last point back to the first on its own, so the last one stays.
  return [...front.slice(0, -1), ...back];
}

function simplifyLine(points: readonly Point[], tolerance: number): Point[] {
  if (points.length < 3) return [...points];

  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return [...points];

  let worst = 0;
  let at = 0;

  for (let index = 1; index < points.length - 1; index += 1) {
    const point = points[index];
    if (!point) continue;

    const away = distanceToLine(point, first, last);
    if (away > worst) {
      worst = away;
      at = index;
    }
  }

  if (worst <= tolerance) return [first, last];

  return [
    ...simplifyLine(points.slice(0, at + 1), tolerance).slice(0, -1),
    ...simplifyLine(points.slice(at), tolerance),
  ];
}

function distanceToLine(point: Point, from: Point, to: Point): number {
  const runX = to[0] - from[0];
  const runZ = to[1] - from[1];
  const length = Math.hypot(runX, runZ);

  // A line of no length: measure to the point itself.
  if (length < 1e-9) return distance(point, from);

  return (
    Math.abs(runZ * point[0] - runX * point[1] + to[0] * from[1] - to[1] * from[0]) /
    length
  );
}

/** Area enclosed by a ring, by the shoelace formula. Always positive. */
export function areaOf(points: readonly Point[]): number {
  let sum = 0;

  for (let index = 0; index < points.length; index += 1) {
    const here = points[index];
    const next = points[(index + 1) % points.length];
    if (!here || !next) continue;
    sum += here[0] * next[1] - next[0] * here[1];
  }

  return Math.abs(sum) / 2;
}

/** Whether a ring is wound anticlockwise in the bed plane. */
export function isAnticlockwise(points: readonly Point[]): boolean {
  let sum = 0;

  for (let index = 0; index < points.length; index += 1) {
    const here = points[index];
    const next = points[(index + 1) % points.length];
    if (!here || !next) continue;
    sum += here[0] * next[1] - next[0] * here[1];
  }

  return sum > 0;
}

function centreOf(points: readonly Point[]): Point {
  let x = 0;
  let z = 0;

  for (const point of points) {
    x += point[0];
    z += point[1];
  }

  return [x / points.length, z / points.length];
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}
